import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Spinner } from '../ui/spinner';
import { Alert, AlertDescription } from '../ui/alert';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';
import { ChevronDown, ChevronRight, RefreshCw, Send } from 'lucide-react';
import { crmCampaignsAPI } from '../../services/api';
import {
  FieldLabel, FREESPIN_COUNT_HINT,
  MAX_WAGERING, MAX_TEST_BET_LEVEL as MAX_BET_LEVEL, MAX_TEST_EMAIL as MAX_EMAIL,
  MAX_TEST_PLATFORM as MAX_PLATFORM, MAX_TEST_TITLE as MAX_TITLE,
  apiError, gamesJsonError, isFreespinCount, isPositiveInt, isValidCurrency,
} from './campaignShared';

// Issue Test Freespins: the top part (Specify Freespins) adds one record to
// campaigns.crm_test_freespins, queued as WAITING; a downstream job sends the bonus and its email
// and sets the status to OK or ERR. The bottom part (Issued Freespins) lists the records created in
// a timeframe, the last month by default.

const PAGE_SIZE = 25;

const EMPTY_FORM = {
  number_of_fs: '', games: '', currency: '', bet_level: '', wagering: '', email: '', platform: '', bonus_title: '',
};

const isWagering = (s) => /^\d+$/.test(s.trim()) && Number(s) <= MAX_WAGERING;
// The backend parses the address properly; this only catches the obvious slips.
const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim()) && s.trim().length <= MAX_EMAIL;

// What is wrong with a field's typed value, or '' when it is fine. An empty value is never wrong
// here: required fields are covered by formProblems when Issue FS is clicked, so nobody is told
// off for a field they have not reached yet.
const fieldProblem = (key, raw) => {
  const v = raw.trim();
  if (v === '') return '';
  switch (key) {
    case 'number_of_fs': return isFreespinCount(v) ? '' : FREESPIN_COUNT_HINT;
    case 'games': return gamesJsonError(v);
    case 'currency': return isValidCurrency(v) ? '' : 'Enter a currency code, e.g. USD.';
    case 'bet_level': return isPositiveInt(v) && Number(v) <= MAX_BET_LEVEL ? '' : `Bet level must be a whole number from 1 to ${MAX_BET_LEVEL}.`;
    case 'wagering': return isWagering(v) ? '' : `Wagering must be a whole number from 0 to ${MAX_WAGERING}.`;
    case 'email': return isEmail(v) ? '' : `Enter a valid email of at most ${MAX_EMAIL} characters.`;
    case 'platform': return v.length <= MAX_PLATFORM ? '' : `Platform can be at most ${MAX_PLATFORM} characters.`;
    case 'bonus_title': return v.length <= MAX_TITLE ? '' : `Bonus title can be at most ${MAX_TITLE} characters.`;
    default: return '';
  }
};

const OPTIONAL_FIELDS = ['currency', 'bet_level', 'platform'];

// Every field's problem when Issue FS is clicked, a required field left empty included.
const formProblems = (f) => Object.fromEntries(Object.keys(f)
  .map((k) => [k, !OPTIONAL_FIELDS.includes(k) && f[k].trim() === '' ? 'Required.' : fieldProblem(k, f[k])])
  .filter(([, p]) => p));

// Games are strings only (gamesJsonError checks), so parsing them cannot round a number.
const toBody = (f) => ({
  number_of_fs: Number(f.number_of_fs),
  games: JSON.parse(f.games),
  currency: f.currency.trim() === '' ? null : f.currency.trim().toUpperCase(),
  bet_level: f.bet_level.trim() === '' ? null : Number(f.bet_level),
  wagering: Number(f.wagering),
  email: f.email.trim(),
  platform: f.platform.trim() === '' ? null : f.platform.trim(),
  bonus_title: f.bonus_title.trim(),
});

const formatGames = (games) => (Array.isArray(games) ? games.join(', ') : JSON.stringify(games));
const formatTime = (t) => (t ? new Date(t).toLocaleString() : '');

// WAITING is neutral yellow, OK green, ERR red; anything else the sendout writes stays plain.
const STATUS_CLASSES = {
  WAITING: 'bg-warning/20 text-warning',
  OK: 'bg-positive/20 text-positive',
  ERR: 'bg-negative/20 text-negative',
};
const STATUSES = ['WAITING', 'OK', 'ERR'];

function StatusBadge({ status }) {
  return <Badge variant="outline" className={STATUS_CLASSES[status] ? `border-0 ${STATUS_CLASSES[status]}` : ''}>{status}</Badge>;
}

// Dates as the date inputs and the API take them, YYYY-MM-DD in local time.
const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const lastMonth = () => {
  const to = new Date();
  const from = new Date(to);
  from.setMonth(from.getMonth() - 1);
  return { from: isoDate(from), to: isoDate(to) };
};

// created_at is set by the database clock, which can be a day ahead of this browser's date (a user
// west of the database's timezone late in their evening). A range ending today therefore asks for
// one day more, so a record just issued is always in the reloaded list.
const apiTo = (to) => {
  const today = new Date();
  if (to < isoDate(today)) return to;
  const next = new Date(`${to}T00:00:00`);
  next.setDate(next.getDate() + 1);
  return isoDate(next);
};

function AddRecord({ onAdded }) {
  const [form, setForm] = useState(EMPTY_FORM);
  // Fields left at least once: a field's problem shows only after that, so typing is not
  // interrupted; once shown, it updates as the value is corrected. Issue FS marks them all.
  const [touched, setTouched] = useState({});
  // What the server's check rejected (the currency lookup, or a rule this form does not mirror),
  // until the form changes.
  const [checkError, setCheckError] = useState(null);
  const [checking, setChecking] = useState(false);
  // The values being confirmed, fixed when the dialog opens.
  const [pending, setPending] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [added, setAdded] = useState(null);

  const set = (key) => (e) => { setForm((f) => ({ ...f, [key]: e.target.value })); setAdded(null); setCheckError(null); };
  const touch = (key) => () => setTouched((t) => (t[key] ? t : { ...t, [key]: true }));
  const typed = Object.values(form).some((v) => v.trim() !== '');
  const reset = () => { setForm(EMPTY_FORM); setTouched({}); setCheckError(null); };

  // Issue FS: every field is checked here, the form's own rules first and then the server's (the
  // currency must be one of the brand's), and the confirmation opens only when all pass.
  const check = async () => {
    setError('');
    setCheckError(null);
    setTouched(Object.fromEntries(Object.keys(form).map((k) => [k, true])));
    if (Object.keys(formProblems(form)).length) return;
    const snapshot = form;
    setChecking(true);
    try {
      await crmCampaignsAPI.checkTestFreespins(toBody(snapshot));
      setPending(snapshot);
    } catch (err) {
      const msg = apiError(err, 'Failed to check the freespins.');
      setCheckError({ field: /^currency/i.test(msg) ? 'currency' : '', msg });
    } finally {
      setChecking(false);
    }
  };

  const issue = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await crmCampaignsAPI.createTestFreespins(toBody(pending));
      setAdded(res.data);
      setPending(null);
      reset();
      onAdded();
    } catch (err) {
      setError(apiError(err, 'Failed to issue the freespins.'));
    } finally {
      setSaving(false);
    }
  };

  const problems = formProblems(form);
  const problemOf = (key) => (touched[key] && problems[key]) || (checkError?.field === key ? checkError.msg : '');
  const problemText = (key) => problemOf(key) && <p className="text-xs font-medium text-negative">{problemOf(key)}</p>;

  const field = (key, label, props = {}) => (
    <label className="space-y-1.5 block">
      <FieldLabel>{label}</FieldLabel>
      <Input value={form[key]} onChange={set(key)} onBlur={touch(key)}
        aria-invalid={Boolean(problemOf(key))} {...props} />
      {problemText(key)}
    </label>
  );

  return (
    <Card>
      <CardContent className="p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {field('number_of_fs', 'Number of FS', { inputMode: 'numeric', placeholder: '1–300' })}
          <label className="space-y-1.5 block md:col-span-3">
            <FieldLabel>Games</FieldLabel>
            <Textarea rows={2} spellCheck={false} className="min-h-0 font-mono text-xs" value={form.games}
              placeholder='["part1:part2"]' onChange={set('games')} onBlur={touch('games')}
              aria-invalid={Boolean(problemOf('games'))} />
            {problemText('games')}
          </label>
          {field('currency', 'Currency (optional)', { placeholder: 'USD' })}
          {field('bet_level', 'Bet level (optional)', { inputMode: 'numeric', placeholder: `1–${MAX_BET_LEVEL}` })}
          {field('wagering', 'Wagering', { inputMode: 'numeric', placeholder: `0–${MAX_WAGERING}` })}
          {field('email', 'Email', { type: 'email', placeholder: 'player@example.com' })}
          {field('platform', 'Platform (optional)')}
          <div className="md:col-span-3">{field('bonus_title', 'Bonus title')}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={check} disabled={!typed || checking}>
            <Send className="h-3.5 w-3.5 mr-1.5" /> {checking ? 'Checking…' : 'Issue FS'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => { reset(); setAdded(null); }} disabled={!typed}>Clear</Button>
          {checkError && !checkError.field && <span className="text-xs font-medium text-negative">{checkError.msg}</span>}
          {added && (
            <span className="text-xs font-medium text-ink-mute">
              Issued #{added.id} for {added.email} as WAITING; it is sent downstream.
            </span>
          )}
        </div>
      </CardContent>

      <Dialog open={Boolean(pending)} onOpenChange={(v) => !v && !saving && setPending(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Issue test freespins?</DialogTitle>
            <DialogDescription>
              Once issued, the bonus and its email go out downstream and can't be taken back here.
            </DialogDescription>
          </DialogHeader>
          {pending && (
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              {[
                ['Number of FS', pending.number_of_fs.trim()],
                ['Games', formatGames(JSON.parse(pending.games))],
                ['Currency', pending.currency.trim().toUpperCase() || '—'],
                ['Bet level', pending.bet_level.trim() || '—'],
                ['Wagering', pending.wagering.trim()],
                ['Email', pending.email.trim()],
                ['Platform', pending.platform.trim() || '—'],
                ['Bonus title', pending.bonus_title.trim()],
              ].map(([k, v]) => (
                <React.Fragment key={k}>
                  <dt className="font-medium text-ink-mute">{k}</dt>
                  <dd className="font-bold text-ink break-all">{v}</dd>
                </React.Fragment>
              ))}
            </dl>
          )}
          {error && <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPending(null)} disabled={saving}>Cancel</Button>
            <Button onClick={issue} disabled={saving}>{saving ? 'Issuing…' : 'Issue FS'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// The two API calls the sendout makes, as [label, result, msg, time] for one row.
const apiCalls = (r) => [
  ['Bonus API', r.bonus_api_call_result, r.bonus_api_call_msg, r.bonus_api_call_time],
  ['Customer.io', r.cio_api_call_result, r.cio_api_call_msg, r.cio_api_call_time],
];

// A status badge whose message and call time show on hover; the expanded row shows them in full.
function ApiStatus({ result, msg, time }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex cursor-default"><StatusBadge status={result} /></span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs space-y-0.5">
        <p className="font-medium break-words">{msg || '—'}</p>
        <p className="font-medium text-ink-mute">{time ? formatTime(time) : 'Not called yet'}</p>
      </TooltipContent>
    </Tooltip>
  );
}

function ResultRow({ r }) {
  const [open, setOpen] = useState(false);
  const Chevron = open ? ChevronDown : ChevronRight;
  const games = Array.isArray(r.games) ? r.games : [formatGames(r.games)];
  const details = [
    ['FS', r.number_of_fs],
    ['Currency', r.currency ?? '—'],
    ['Bet level', r.bet_level ?? '—'],
    ['Wagering', r.wagering],
  ];

  return (
    <>
      <TableRow className={open ? 'border-b-0' : ''}>
        <TableCell className="align-top pr-0">
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setOpen((o) => !o)}
            aria-expanded={open} aria-label={open ? 'Hide API details' : 'Show API details'}>
            <Chevron className="h-4 w-4 text-ink-mute" />
          </Button>
        </TableCell>
        <TableCell className="align-top text-xs whitespace-nowrap">
          <p className="font-bold text-ink">{formatTime(r.created_at)}</p>
          {r.created_by && <p className="font-medium text-ink-mute">{r.created_by}</p>}
        </TableCell>
        <TableCell className="align-top text-xs">
          <p className="font-bold text-ink break-all">{r.email}</p>
          {r.platform && <p className="font-medium text-ink-mute">{r.platform}</p>}
        </TableCell>
        <TableCell className="align-top text-xs">
          <p className="mb-1 font-bold text-ink">{r.bonus_title}</p>
          <dl className="grid grid-cols-[auto_auto] justify-start gap-x-3 gap-y-0.5">
            {details.map(([k, v]) => (
              <React.Fragment key={k}>
                <dt className="font-medium text-ink-mute">{k}</dt>
                <dd className="font-mono font-medium text-ink">{v}</dd>
              </React.Fragment>
            ))}
          </dl>
        </TableCell>
        <TableCell className="align-top text-xs">
          <ul className="space-y-0.5 font-mono font-medium text-ink">
            {games.map((g, i) => <li key={i} className="whitespace-nowrap">{g}</li>)}
          </ul>
        </TableCell>
        {apiCalls(r).map(([label, result, msg, time]) => (
          <TableCell key={label} className="align-top">
            <ApiStatus result={result} msg={msg} time={time} />
          </TableCell>
        ))}
      </TableRow>
      {open && (
        <TableRow className="hover:bg-transparent">
          <TableCell />
          <TableCell colSpan={6} className="pt-0">
            <div className="grid gap-3 rounded-md bg-surface-2 p-3 sm:grid-cols-2">
              {apiCalls(r).map(([label, result, msg, time]) => (
                <div key={label} className="space-y-1 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-ink">{label}</span>
                    <StatusBadge status={result} />
                  </div>
                  <p className="font-medium text-ink-dim break-words">{msg || '—'}</p>
                  <p className="font-medium text-ink-mute">{time ? `Called ${formatTime(time)}` : 'Not called yet'}</p>
                </div>
              ))}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function Results({ reloadKey }) {
  const [range, setRange] = useState(lastMonth);
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const rangeValid = Boolean(range.from && range.to) && range.from <= range.to;

  const load = useCallback(async () => {
    if (!rangeValid) return;
    setLoading(true);
    setError('');
    const params = { from: range.from, to: apiTo(range.to), page, page_size: PAGE_SIZE };
    if (status !== 'all') params.status = status;
    try {
      const res = await crmCampaignsAPI.listTestFreespins(params);
      if (!Array.isArray(res.data?.rows)) throw new Error('unexpected response');
      setData(res.data);
      // Rows can leave a status filter between loads (the sendout flips WAITING to OK), so the
      // page may now be past the end: move to the last one, which loads it.
      const last = Math.max(1, Math.ceil(res.data.total / PAGE_SIZE));
      if (page > last) setPage(last);
    } catch (err) {
      setError(apiError(err, 'Failed to load issued freespins.'));
    } finally {
      setLoading(false);
    }
  }, [range, status, page, rangeValid]);

  useEffect(() => { load(); }, [load]);
  // A new record lands on page 1: load page 1 once, either directly or by moving there (which
  // loads through the effect above). The ref keeps this effect to reloadKey alone.
  const latest = useRef({ page, load });
  latest.current = { page, load };
  useEffect(() => {
    if (!reloadKey) return;
    if (latest.current.page === 1) latest.current.load();
    else setPage(1);
  }, [reloadKey]);

  const setDate = (key) => (e) => { setRange((r) => ({ ...r, [key]: e.target.value })); setPage(1); };
  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="space-y-1.5 block">
          <FieldLabel>Created from</FieldLabel>
          <Input type="date" className="h-8 w-40 text-xs" value={range.from} max={range.to || undefined} onChange={setDate('from')} />
        </label>
        <label className="space-y-1.5 block">
          <FieldLabel>Created to</FieldLabel>
          <Input type="date" className="h-8 w-40 text-xs" value={range.to} min={range.from || undefined} onChange={setDate('to')} />
        </label>
        <div className="space-y-1.5">
          <FieldLabel>Bonus status</FieldLabel>
          <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
            <SelectTrigger className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button variant="ghost" size="sm" onClick={() => { setRange(lastMonth()); setStatus('all'); setPage(1); }}>Last month</Button>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={load} disabled={loading || !rangeValid}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </div>
      {!rangeValid && <p className="text-xs font-medium text-negative">Pick both dates, with the start not after the end.</p>}
      {error && <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-10"><Spinner /></div>
          ) : data.rows.length === 0 ? (
            <p className="py-10 text-center text-sm font-medium text-ink-dim">No records in this timeframe.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Created</TableHead>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Bonus</TableHead>
                  <TableHead>Games</TableHead>
                  <TableHead>Bonus API</TableHead>
                  <TableHead>Customer.io</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r) => <ResultRow key={r.id} r={r} />)}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-3 text-xs font-medium text-ink-dim">
          <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>Previous</Button>
          <span>Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}

const TestFreespinsPanel = () => {
  // Bumped after each add, so the results reload and show the new record.
  const [reloadKey, setReloadKey] = useState(0);

  return (
    <div className="space-y-10">
      <section className="space-y-4">
        <div>
          <h3 className="text-base font-bold text-ink">Specify Freespins</h3>
          <p className="text-xs font-medium text-ink-dim">Queues one freespin bonus as WAITING. The bonus and its email are sent downstream.</p>
        </div>
        <AddRecord onAdded={() => setReloadKey((k) => k + 1)} />
      </section>

      <section className="space-y-4 border-t border-white/10 pt-8">
        <div>
          <h3 className="text-base font-bold text-ink">Issued Freespins</h3>
          <p className="text-xs font-medium text-ink-dim">Records by creation date, the last month unless you pick another timeframe.</p>
        </div>
        <Results reloadKey={reloadKey} />
      </section>
    </div>
  );
};

export default TestFreespinsPanel;
